import React, { useState, useEffect } from 'react';

const DownloadPanel = ({ onDownloadStart, onFolderSelect, downloadPath, downloadState, aiFeatures, onAiFeatureToggle }) => {
  const [url, setUrl] = useState('');
  const [urls, setUrls] = useState([]);
  const [quality, setQuality] = useState('1080p');
  const [downloadSubtitles, setDownloadSubtitles] = useState(false);
  const [extractAudio, setExtractAudio] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [downloadQueue, setDownloadQueue] = useState([]);
  const [currentDownload, setCurrentDownload] = useState(null);
  const [subtitleLanguage, setSubtitleLanguage] = useState('en');
  const [outputFormat, setOutputFormat] = useState('mp4');
  const [audioFormat, setAudioFormat] = useState('mp3');
  const [darkMode, setDarkMode] = useState(true);
  const [estimatedSize, setEstimatedSize] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [dailyDownloads, setDailyDownloads] = useState(0);
  const [isProUser, setIsProUser] = useState(false);

  const qualityOptions = [
    { value: '8K', label: '8K Ultra HD (4320p) - Maximum Quality' },
    { value: '4K', label: '4K Ultra HD (2160p) - Best Quality' },
    { value: '1080p', label: '1080p Full HD - High Quality' },
    { value: '720p', label: '720p HD - Good Quality' },
    { value: '480p', label: '480p Standard - Medium Quality' },
    { value: 'audio', label: 'Audio Only (MP3)' }
  ];

  const subtitleLanguages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ru', label: 'Russian' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'zh', label: 'Chinese' },
    { value: 'ar', label: 'Arabic' },
    { value: 'hi', label: 'Hindi' },
    { value: 'ta', label: 'Tamil' }
  ];

  const videoFormats = [
    { value: 'mp4', label: 'MP4 (Recommended)' },
    { value: 'mkv', label: 'MKV (High Quality)' },
    { value: 'mov', label: 'MOV (Apple)' },
    { value: 'webm', label: 'WebM (Web)' },
    { value: 'avi', label: 'AVI (Legacy)' }
  ];

  const audioFormats = [
    { value: 'mp3', label: 'MP3 (Universal)' },
    { value: 'wav', label: 'WAV (Lossless)' },
    { value: 'aac', label: 'AAC (Apple)' },
    { value: 'flac', label: 'FLAC (Lossless)' },
    { value: 'ogg', label: 'OGG (Open)' }
  ];

  // Auto-detect clipboard
  useEffect(() => {
    const handlePaste = (e) => {
      const text = e.clipboardData.getData('text');
      if (text && (text.includes('youtube.com') || text.includes('youtu.be') || text.includes('tiktok.com') || text.includes('instagram.com'))) {
        setUrl(text);
        setPreviewData({ url: text, title: 'Loading preview...', duration: '0:00', thumbnail: '' });
        // Simulate preview loading
        setTimeout(() => {
          setPreviewData({
            url: text,
            title: 'Sample Video Title - Auto-detected from clipboard!',
            duration: '5:23',
            thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjM0Q0NTcxIi8+CjxwYXRoIGQ9Ik0xNDAgODBMMTYwIDEwMEwxNDAgMTIwVjgwWiIgZmlsbD0iI0ZGRiIvPgo8dGV4dCB4PSIxNjAiIHk9IjE0MCIgZmlsbD0iI0ZGRiIgZm9udC1zaXplPSIxNCI+VmlkZW8gUHJldmlldzwvdGV4dD4KPC9zdmc+'
          });
        }, 1000);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Load daily download count
  useEffect(() => {
    const saved = localStorage.getItem('dailyDownloads');
    const today = new Date().toDateString();
    const lastReset = localStorage.getItem('lastReset');
    
    if (lastReset !== today) {
      setDailyDownloads(0);
      localStorage.setItem('dailyDownloads', '0');
      localStorage.setItem('lastReset', today);
    } else {
      setDailyDownloads(parseInt(saved) || 0);
    }
  }, []);

  const handleStartDownload = () => {
    if (!url.trim()) {
      alert('Please enter a video URL');
      return;
    }

    // Check daily limit for free users
    if (!isProUser && dailyDownloads >= 5) {
      alert('Daily limit reached! Upgrade to Pro for unlimited downloads.');
      return;
    }

    if (batchMode && urls.length > 0) {
      // Start batch download
      const queue = urls.map((urlItem, index) => ({
        id: Date.now() + index,
        url: urlItem,
        quality,
        status: 'pending',
        progress: 0
      }));
      setDownloadQueue(queue);
      setCurrentDownload(queue[0]);
    } else {
      // Single download
      onDownloadStart({
        url: url.trim(),
        quality,
        options: {
          downloadSubtitles,
          extractAudio,
          subtitleLanguage,
          outputFormat,
          audioFormat,
          ...aiFeatures
        }
      });
    }

    // Update daily count
    setDailyDownloads(prev => {
      const newCount = prev + 1;
      localStorage.setItem('dailyDownloads', newCount.toString());
      return newCount;
    });
  };

  const handleAddUrl = () => {
    if (url.trim() && !urls.includes(url.trim())) {
      setUrls([...urls, url.trim()]);
      setUrl('');
    }
  };

  const handleRemoveUrl = (index) => {
    setUrls(urls.filter((_, i) => i !== index));
  };

  const handlePasteMultiple = () => {
    const text = prompt('Paste multiple URLs (one per line):');
    if (text) {
      const urlList = text.split('\n').filter(u => u.trim());
      setUrls([...urls, ...urlList]);
    }
  };

  const getEstimatedSize = () => {
    const sizes = {
      '8K': '10-20 GB',
      '4K': '2-5 GB',
      '1080p': '500MB-2GB',
      '720p': '200-500MB',
      '480p': '100-200MB',
      'audio': '3-10MB'
    };
    return sizes[quality] || 'Unknown';
  };

  const getSpeedEstimate = () => {
    const speeds = {
      '8K': '15-30 min',
      '4K': '5-15 min',
      '1080p': '2-8 min',
      '720p': '1-5 min',
      '480p': '30s-2 min',
      'audio': '10-30s'
    };
    return speeds[quality] || 'Unknown';
  };

  return (
    <div className={`download-panel ${darkMode ? 'dark' : 'light'}`}>
      {/* Demo Watermark */}
      <div className="demo-watermark">
        <span className="watermark-text">🚀 DEMO VERSION - Full features in Pro</span>
      </div>

      {/* Mode Toggle */}
      <div className="panel-section">
        <div className="mode-toggle">
          <button 
            className={`mode-btn ${!batchMode ? 'active' : ''}`}
            onClick={() => setBatchMode(false)}
          >
            Single Download
          </button>
          <button 
            className={`mode-btn ${batchMode ? 'active' : ''}`}
            onClick={() => setBatchMode(true)}
          >
            Batch Download ({urls.length})
          </button>
        </div>
      </div>

      {/* Video URL Section */}
      <div className="panel-section">
        <h3 className="section-title">🔗 Video URL</h3>
        <div className="url-input-container">
          <input
            type="text"
            className="url-input"
            placeholder="Paste video URLs here (auto-detects from clipboard)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onPaste={(e) => {
              setTimeout(() => {
                const text = e.target.value;
                if (text && (text.includes('youtube.com') || text.includes('youtu.be'))) {
                  setPreviewData({
                    url: text,
                    title: 'Loading preview...',
                    duration: '0:00',
                    thumbnail: ''
                  });
                }
              }, 100);
            }}
          />
          <button className="add-url-btn" onClick={handleAddUrl}>
            Add
          </button>
        </div>

        {/* Batch URL List */}
        {batchMode && urls.length > 0 && (
          <div className="url-list">
            <h4>URL Queue ({urls.length})</h4>
            {urls.map((urlItem, index) => (
              <div key={index} className="url-item">
                <span className="url-text">{urlItem}</span>
                <button 
                  className="remove-url-btn"
                  onClick={() => handleRemoveUrl(index)}
                >
                  ×
                </button>
              </div>
            ))}
            <button className="paste-multiple-btn" onClick={handlePasteMultiple}>
              📋 Paste Multiple URLs
            </button>
          </div>
        )}

        {/* Video Preview */}
        {previewData && (
          <div className="video-preview">
            <div className="preview-thumbnail">
              <img src={previewData.thumbnail} alt="Video thumbnail" />
              <div className="play-overlay">▶</div>
            </div>
            <div className="preview-info">
              <h4>{previewData.title}</h4>
              <p>Duration: {previewData.duration}</p>
              <p>Estimated Size: {getEstimatedSize()}</p>
              <p>Time: {getSpeedEstimate()}</p>
            </div>
          </div>
        )}
      </div>

      {/* Quality Section */}
      <div className="panel-section">
        <h3 className="section-title">🎚 Quality & Format</h3>
        <div className="quality-controls">
          <select
            className="quality-select"
            value={quality}
            onChange={(e) => setQuality(e.target.value)}
          >
            {qualityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="browse-button" onClick={onFolderSelect}>
            📁 Browse
          </button>
        </div>
        
        <div className="format-controls">
          <div className="format-group">
            <label>Video Format:</label>
            <select
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
            >
              {videoFormats.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label}
                </option>
              ))}
            </select>
          </div>
          <div className="format-group">
            <label>Audio Format:</label>
            <select
              value={audioFormat}
              onChange={(e) => setAudioFormat(e.target.value)}
            >
              {audioFormats.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="quality-info">
          <span className="quality-desc">
            {quality === '8K' && '🚀 8K Ultra-high definition - Maximum quality (large file size)'}
            {quality === '4K' && '🎬 4K Ultra-high definition video with best audio quality'}
            {quality === '1080p' && '📺 Full HD video with high-quality audio'}
            {quality === '720p' && '📱 HD video with good audio quality'}
            {quality === '480p' && '💻 Standard definition video with decent audio'}
            {quality === 'audio' && '🎵 High-quality MP3 audio only'}
          </span>
        </div>
        
        <div className="path-display">
          <span className="path-label">Save to:</span>
          <span className="path-text">{downloadPath}</span>
        </div>
      </div>

      {/* Download Options */}
      <div className="panel-section">
        <h3 className="section-title">⚙️ Download Options</h3>
        <div className="checkbox-group">
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={downloadSubtitles}
              onChange={(e) => setDownloadSubtitles(e.target.checked)}
            />
            <span className="checkbox-label">📝 Download Subtitles</span>
          </label>
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={extractAudio}
              onChange={(e) => setExtractAudio(e.target.checked)}
            />
            <span className="checkbox-label">🎵 Extract Audio ({audioFormat.toUpperCase()})</span>
          </label>
        </div>

        {downloadSubtitles && (
          <div className="subtitle-language">
            <label>Translate to:</label>
            <select
              value={subtitleLanguage}
              onChange={(e) => setSubtitleLanguage(e.target.value)}
            >
              {subtitleLanguages.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* AI Features */}
      <div className="panel-section">
        <h3 className="section-title">🧠 AI Features</h3>
        <div className="checkbox-group">
          <label className="checkbox-item ai-feature">
            <input
              type="checkbox"
              checked={aiFeatures.autoRename}
              onChange={(e) => onAiFeatureToggle({...aiFeatures, autoRename: e.target.checked})}
            />
            <span className="checkbox-label">🤖 AI Auto Rename (Title_Channel_Date_Quality)</span>
          </label>
          <label className="checkbox-item ai-feature">
            <input
              type="checkbox"
              checked={aiFeatures.autoTranslateSubs}
              onChange={(e) => onAiFeatureToggle({...aiFeatures, autoTranslateSubs: e.target.checked})}
            />
            <span className="checkbox-label">🌐 AI Subtitle Translate</span>
          </label>
          <label className="checkbox-item ai-feature">
            <input
              type="checkbox"
              checked={aiFeatures.autoFolderSort}
              onChange={(e) => onAiFeatureToggle({...aiFeatures, autoFolderSort: e.target.checked})}
            />
            <span className="checkbox-label">📁 Auto Folder Sort (Music/Movies/Tutorials)</span>
          </label>
          <label className="checkbox-item ai-feature">
            <input
              type="checkbox"
              checked={aiFeatures.cloudSync}
              onChange={(e) => onAiFeatureToggle({...aiFeatures, cloudSync: e.target.checked})}
            />
            <span className="checkbox-label">☁️ Cloud Upload (Google Drive/Dropbox)</span>
          </label>
        </div>
      </div>

      {/* Batch Download Progress */}
      {batchMode && downloadQueue.length > 0 && (
        <div className="panel-section">
          <h3 className="section-title">📊 Batch Progress</h3>
          <div className="batch-progress">
            {downloadQueue.map((item, index) => (
              <div key={item.id} className="queue-item">
                <div className="queue-info">
                  <span className="queue-url">{item.url}</span>
                  <span className="queue-status">{item.status}</span>
                </div>
                <div className="queue-progress">
                  <div 
                    className="queue-progress-bar"
                    style={{ width: `${item.progress}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Download Progress */}
      <div className="panel-section">
        <h3 className="section-title">📊 Download Progress</h3>
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${downloadState.progress}%` }}
            ></div>
          </div>
          <div className="progress-text">{downloadState.status}</div>
        </div>
      </div>

      {/* Daily Limit Warning */}
      {!isProUser && dailyDownloads >= 3 && (
        <div className="limit-warning">
          <span>⚠️ {5 - dailyDownloads} downloads remaining today</span>
          <button className="upgrade-btn" onClick={() => alert('Upgrade to Pro for unlimited downloads!')}>
            Upgrade to Pro
          </button>
        </div>
      )}

      {/* Control Buttons */}
      <div className="panel-section">
        <div className="button-group">
          <button 
            className="btn btn-primary start-btn"
            onClick={handleStartDownload}
            disabled={downloadState.isDownloading || (!isProUser && dailyDownloads >= 5)}
          >
            {batchMode ? `Start Batch (${urls.length})` : 'Start Download'}
          </button>
          <button 
            className="btn btn-secondary pause-btn"
            onClick={() => console.log('Pause download')}
            disabled={!downloadState.isDownloading}
          >
            Pause
          </button>
          <button 
            className="btn btn-secondary cancel-btn"
            onClick={() => console.log('Cancel download')}
            disabled={!downloadState.isDownloading}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Theme Toggle */}
      <div className="theme-toggle">
        <button 
          className={`theme-btn ${darkMode ? 'active' : ''}`}
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? '🌙' : '☀️'} {darkMode ? 'Dark' : 'Light'} Mode
        </button>
      </div>
    </div>
  );
};

export default DownloadPanel;